import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Checkbox,
    FormControlLabel,
    Button,
    Tabs,
    Tab,
    CircularProgress,
    Divider,
    Switch,
    Stack,
    Alert,
    Chip,
    TextField,
    MenuItem,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Shield as ShieldIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config/api';

const ROLES = [
    'SCHOOL_ADMIN',
    'PRINCIPAL',
    'BURSAR',
    'ACCOUNTANT',
    'TEACHER',
    'RECEPTIONIST',
    'PARENT',
    'STUDENT',
];

const RolePermissionsPage: React.FC = () => {
    const [activeRole, setActiveRole] = useState('BURSAR');
    const [permissions, setPermissions] = useState<any[]>([]);
    const [rolePermissions, setRolePermissions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [tabMode, setTabMode] = useState(0); // 0 = Roles, 1 = Users
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState<any>(null);

    const config = React.useMemo(() => ({
        headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('userInfo') || '{}').token}`,
        },
    }), []);

    const fetchPermissions = React.useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/permissions`, config);
            setPermissions(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setLoading(false);
        }
    }, [config]);

    const fetchRolePermissions = React.useCallback(async (role: string) => {
        setRolePermissions([]); // CRITICAL: Clear before fetch to prevent stale state leakage
        setLoading(true);
        try {
            // FIX: Corrected URL from /permissions/role/${role} to /permissions/${role}
            const { data } = await axios.get(`${API_URL}/permissions/${role}`, config);
            setRolePermissions(data.map((p: any) => p.id));
        } catch (error) {
            console.error('Error fetching role permissions:', error);
            setRolePermissions([]); // Clear on error too
        } finally {
            setLoading(false);
        }
    }, [config]);

    const fetchUsers = React.useCallback(async (search: string) => {
        if (search.length < 2) return;
        try {
            const { data } = await axios.get(`${API_URL}/users`, config);
            const filtered = data.filter((u: any) =>
                u.name.toLowerCase().includes(search.toLowerCase()) ||
                u.email.toLowerCase().includes(search.toLowerCase())
            );
            setUsers(filtered);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, [config]);

    const fetchUserPermissions = React.useCallback(async (userId: string) => {
        setRolePermissions([]);
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/users`, config);
            const user = data.find((u: any) => u.id === userId);
            if (user) {
                setRolePermissions(user.permissions?.map((p: any) => p.permissionId) || []);
            }
        } catch (error) {
            console.error('Error fetching user permissions:', error);
        } finally {
            setLoading(false);
        }
    }, [config]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    useEffect(() => {
        if (activeRole) {
            setCanEdit(false); // Reset lock when changing roles
            fetchRolePermissions(activeRole);
        }
    }, [activeRole, fetchRolePermissions]);

    const handleToggle = (permissionId: string) => {
        setRolePermissions((prev) =>
            prev.includes(permissionId)
                ? prev.filter((id) => id !== permissionId)
                : [...prev, permissionId]
        );
    };

    const handleSave = async () => {
        const targetName = tabMode === 0 ? `Role: ${activeRole}` : `User: ${selectedUser?.name}`;
        if (window.confirm(`Are you sure you want to update permissions for ${targetName}?`)) {
            setSaving(true);
            try {
                if (tabMode === 0) {
                    await axios.post(`${API_URL}/permissions/update`, {
                        role: activeRole,
                        permissionIds: rolePermissions,
                    }, config);
                } else {
                    await axios.put(`${API_URL}/users/${selectedUser.id}`, {
                        ...selectedUser,
                        permissionIds: rolePermissions,
                    }, config);
                }
                setCanEdit(false);
                alert('Permissions updated successfully!');
            } catch (error) {
                console.error('Error saving permissions:', error);
                alert('Failed to save permissions.');
            } finally {
                setSaving(false);
            }
        }
    };

    // Group permissions by module
    const modules = Array.from(new Set(permissions.map((p) => p.module)));

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                Role Permissions Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Configure which modules and actions each role can access.
            </Typography>

            {!canEdit && (
                <Alert
                    severity="info"
                    icon={<ShieldIcon />}
                    sx={{ mb: 3, borderRadius: '12px' }}
                >
                    Permissions are currently <strong>Locked</strong>. Toggle "Allow Changes" below if you need to modify this role's access.
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabMode} onChange={(_, v) => {
                    setTabMode(v);
                    setCanEdit(false);
                    setRolePermissions([]);
                    setSelectedUser(null);
                    setSearchTerm('');
                }}>
                    <Tab label="Role-Based Access" />
                    <Tab label="Individual Person Overrides" />
                </Tabs>
            </Box>

            <Paper sx={{ mb: 4, borderRadius: '16px' }}>
                {tabMode === 0 ? (
                    <Tabs
                        value={activeRole}
                        onChange={(_, newValue) => setActiveRole(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                    >
                        {ROLES.map((role) => (
                            <Tab key={role} label={role.replace('_', ' ')} value={role} />
                        ))}
                    </Tabs>
                ) : (
                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <TextField
                            fullWidth
                            label="Search Staff Name or Email"
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                fetchUsers(e.target.value);
                            }}
                            placeholder="Type to search..."
                        />
                        {users.length > 0 && !selectedUser && (
                            <Paper elevation={3} sx={{ mt: 1, position: 'absolute', zIndex: 10, width: 'calc(100% - 96px)', maxWidth: '500px' }}>
                                {users.map((u: any) => (
                                    <MenuItem key={u.id} onClick={() => {
                                        setSelectedUser(u);
                                        setSearchTerm(u.name);
                                        setUsers([]);
                                        fetchUserPermissions(u.id);
                                    }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">{u.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{u.email} ({u.role})</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Paper>
                        )}
                        {selectedUser && (
                            <Chip
                                label={`Selected: ${selectedUser.name}`}
                                color="primary"
                                onDelete={() => {
                                    setSelectedUser(null);
                                    setSearchTerm('');
                                    setRolePermissions([]);
                                }}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: canEdit ? 'rgba(255, 171, 0, 0.05)' : '#f8f9fa', borderRadius: '12px', border: '1px solid', borderColor: canEdit ? 'secondary.main' : 'divider' }}>
                        <Box>
                            <Typography variant="h6" fontWeight="700" color={canEdit ? 'secondary.main' : 'text.primary'}>
                                {tabMode === 0 ? `Module Access for ${activeRole.replace('_', ' ')}` : `Custom Overrides for ${selectedUser?.name || 'Selected User'}`}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="medium"
                                            checked={canEdit}
                                            onChange={(e) => setCanEdit(e.target.checked)}
                                            color="secondary"
                                        />
                                    }
                                    label={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {canEdit ? <LockOpenIcon color="secondary" fontSize="small" /> : <LockIcon color="action" fontSize="small" />}
                                            <Typography variant="body2" fontWeight="700">
                                                {canEdit ? 'Changes Allowed' : 'Allow Changes'}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </Stack>
                        </Box>
                        <Button
                            variant="contained"
                            color={canEdit ? "primary" : "inherit"}
                            onClick={handleSave}
                            disabled={saving || !canEdit}
                            startIcon={<ShieldIcon />}
                            sx={{ borderRadius: '8px', px: 4, py: 1.5, fontWeight: 'bold' }}
                        >
                            {saving ? 'Saving...' : 'Apply & Save'}
                        </Button>
                    </Box>

                    <Grid container spacing={4}>
                        {modules.map((module) => (
                            <Grid size={{ xs: 12, md: 6 }} key={module}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                                        {module}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {permissions
                                            .filter((p) => p.module === module)
                                            .map((p) => (
                                                <FormControlLabel
                                                    key={p.id}
                                                    control={
                                                        <Checkbox
                                                            checked={rolePermissions.includes(p.id)}
                                                            onChange={() => handleToggle(p.id)}
                                                            disabled={!canEdit}
                                                            color="secondary"
                                                            sx={{
                                                                '&.Mui-disabled': {
                                                                    color: rolePermissions.includes(p.id) ? 'secondary.main' : 'action.disabled',
                                                                    opacity: rolePermissions.includes(p.id) ? 1 : 0.38
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="600">
                                                                {p.name}
                                                            </Typography>
                                                            {p.description && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {p.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default RolePermissionsPage;
